/**
 * The public booking API (contracts booking.ts; routes publicBookingPage … publicBookingCancel): no account, no
 * session token. Writes carry the Turnstile token. Mock mode answers from platform/mock/booking.ts.
 */
import type { BookingSlotsResponse, BookRequest, BookResponse, PublicBooking, PublicBookingPage } from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind } from "@bundle/core";
import { API_BASE } from "./env.ts";

export type BookingApi = {
  page(code: string): Promise<PublicBookingPage>;
  slots(code: string, from: number, to: number): Promise<BookingSlotsResponse>;
  verify(code: string, email: string, turnstileToken?: string): Promise<void>;
  book(code: string, req: BookRequest): Promise<BookResponse>;
  booking(token: string): Promise<PublicBooking>;
  reschedule(token: string, start: number, turnstileToken?: string): Promise<BookResponse>;
  cancel(token: string, reason?: string): Promise<PublicBooking>;
};

function kindFor(status: number): ApiErrorKind {
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, query = ""): Promise<T> {
  const route = routes[name];
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(API_BASE + buildPath(route.path, params) + query, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (e) {
    throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text === "" ? undefined : JSON.parse(text);
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
  }
  return json as T;
}

export async function createBookingApi(mock: boolean): Promise<BookingApi> {
  if (mock) {
    const { mockBooking: m } = await import("./mock/booking.ts");
    // Seed the mock pages so /book/<code> from the sidebar resolves in the dev server.
    m.list("mock");
    const later = <T>(fn: () => T) => new Promise<T>((resolve, reject) => setTimeout(() => { try { resolve(fn()); } catch (e) { reject(e); } }, 150));
    return {
      page: (code) => later(() => m.page(code)),
      slots: (code, from, to) => later(() => m.slots(code, from, to)),
      verify: () => later(() => undefined),
      book: (code, req) => later(() => m.book(code, req)),
      booking: (token) => later(() => m.booking(token)),
      reschedule: (token, start) => later(() => m.reschedule(token, start)),
      cancel: (token, reason) => later(() => m.cancel(token, reason)),
    };
  }
  return {
    page: (code) => call("publicBookingPage", { code }),
    slots: (code, from, to) => call("publicBookingSlots", { code }, undefined, `?from=${from}&to=${to}`),
    verify: (code, email, turnstileToken) => call("publicBookingVerify", { code }, turnstileToken === undefined ? { email } : { email, turnstileToken }),
    book: (code, req) => call("publicBook", { code }, req),
    booking: (token) => call("publicBooking", { token }),
    reschedule: (token, start, turnstileToken) => call("publicBookingReschedule", { token }, turnstileToken === undefined ? { start } : { start, turnstileToken }),
    cancel: (token, reason) => call("publicBookingCancel", { token }, reason === undefined || reason.trim() === "" ? {} : { reason: reason.trim() }),
  };
}

/** `/book/<code>` → the code. */
export function bookingCodeFromPath(path: string): string | undefined {
  const m = /^\/book\/([A-Za-z0-9_-]{1,40})\/?$/.exec(path);
  return m?.[1];
}

/** `/booking/<token>` → the manage token. */
export function bookingTokenFromPath(path: string): string | undefined {
  const m = /^\/booking\/([A-Za-z0-9_.-]{1,200})\/?$/.exec(path);
  return m?.[1];
}
